/* script */
//

/**
 * This component is used to display an alert notification in case the event
 * needs an important update.
 *
 * @vue-prop {String} eventId the event ID
 * @vue-prop {Object[]} [updates=[]] a list of updateable packages
 *
 * @category ui.components
 * @exports CriticalUpdates
 */
const __vue_script__ = {
    name: 'CriticalUpdates',

    props: {
        event: {
            type: Object,
            required: true
        },

        updates: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            meetsTimeRequirements: this.checkRequirements()
        };
    },


    watch: {
        updates: {
            immediate: true,
            handler() {
                const updatable = this.updates.find(u => u.is_critical);

                if (updatable) {
                    this.showNotification(updatable);
                }
            }
        }
    },

    methods: {
        /**
         * This method checks whether the critical update notification should be
         * displayed or not based on the current time and the begin/end of the
         * given event.
         *
         * @returns {Boolean} true if we should display notification false otherwise
         *
         * @private
         */
        checkRequirements() {
            const { now: timeNow, dateIsAfter, dateIsBefore, timeDiff } = this.$utils.time;
            const { startdate, enddate } = this.event;
            const now = timeNow();

            if (dateIsAfter(now, enddate)) {
                // The event's over
                return false;
            }

            // Check if it's a long lasting event
            const duration = timeDiff(enddate, startdate, 'days');
            const endsInDays = timeDiff(enddate, now, 'days');

            if (duration >= 30) {
                // the event lasts at least a month
                if (endsInDays < 7) {
                    // but ends in less than 1 week
                    return true;
                }

                return false;
            }

            // 1a. event will start in less than one week from now
            const startsInDays = timeDiff(startdate, now, 'days');

            // 1b. event has started
            const isStarted = dateIsBefore(startdate, now);

            return isStarted || startsInDays < 7;
        },

        /**
         * Given an updatable package this method returns its storage key.
         *
         * @param {Object} update the updatable information
         *
         * @returns {String} the given update's storage key
         */
        getStorageKey(update) {
            return `${this.$const.CRITICAL_UPDATE_STORAGE_KEY}-${update.name}@${update.patch}`;
        },

        /**
         * Given an updatable package this method marks its read status as true on the storage service.
         *
         * @param {Object} update the updatable information
         */
        markAsRead(update) {
            this.$services.storage.set(this.getStorageKey(update), true);
        },

        /**
         * Given an updatable package this method returns its read status.
         *
         * @param {Object} update the updatable information
         *
         * @returns {Boolean} the given update's read status
         */
        getReadStatus(update) {
            return this.$services.storage.get(this.getStorageKey(update));
        },

        /**
         * Pops the notification up
         *
         * @param {Object} updatable the updatable information
         */
        showNotification(updatable) {
            if (!this.meetsTimeRequirements || this.getReadStatus(updatable)) {
                return;
            }

            this.$notify({
                group: 'flashes',
                title: this.$i18n.t('marketplace.critical_updates.notification.title'),
                text: this.$i18n.t('marketplace.critical_updates.notification.message'),
                type: 'warning',
                duration: -1,
                data: {
                    button: this.$i18n.t('marketplace.critical_updates.notification.dismiss_button'),
                    action: props => {
                        const path = this.$const.MARKETPLACE_MAIN_ROUTE.replace('{{eventId}}', this.event._id);

                        this.$router.push(path, {}, { category: 'installed' });
                        this.markAsRead(updatable);
                        props.close();
                    }
                }
            });
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c("div")}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "CriticalUpdates.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__